import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, ChevronRight, CheckCircle, XCircle, 
  RotateCcw, Award, ArrowLeft, BookOpen, Lock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizPage = () => {
  const { isAuthenticated } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const res = await axios.get(`${API}/quizzes`);
      setQuizzes(res.data.quizzes || []);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
    }
    setLoading(false);
  };

  const startQuiz = async (quizId) => {
    try {
      const res = await axios.get(`${API}/quizzes/${quizId}`);
      setActiveQuiz(res.data);
      setQuestions(res.data.questions);
      setCurrentQ(0);
      setAnswers({});
      setResults(null);
    } catch (err) {
      console.error('Error loading quiz:', err);
    }
  };

  const selectAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quizzes/submit`, {
        quiz_id: activeQuiz.id,
        answers
      });
      setResults(res.data);
    } catch (err) {
      console.error('Error submitting quiz:', err);
    }
    setSubmitting(false);
  };

  const resetQuiz = () => {
    setActiveQuiz(null);
    setQuestions([]);
    setAnswers({});
    setResults(null);
    setCurrentQ(0);
  };

  if (loading) {
    return (
      <div className="quiz-loading" data-testid="quiz-loading">
        <div className="spinner" />
        <p>Loading quizzes...</p>
      </div>
    );
  }

  return (
    <div className="quiz-page" data-testid="quiz-page">
      {!activeQuiz ? (
        <>
          <section className="quiz-hero">
            <motion.div className="quiz-hero-content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <ClipboardCheck size={48} />
              <h1>Knowledge Quizzes</h1>
              <p>Test your trading knowledge and track your learning progress.</p>
            </motion.div>
          </section>

          <section className="quiz-list" data-testid="quiz-list">
            <div className="quiz-grid">
              {quizzes.map((quiz, i) => (
                <motion.div
                  key={quiz.id}
                  className="quiz-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`quiz-card-${quiz.id}`}
                >
                  <div className="quiz-card-header">
                    <span className={`quiz-level level-${quiz.level}`}>{quiz.level}</span>
                    <span className="quiz-count">{quiz.question_count} questions</span>
                  </div>
                  <h3>{quiz.title}</h3>
                  <p>{quiz.description}</p>
                  <button 
                    className="btn btn-primary quiz-start-btn" 
                    onClick={() => startQuiz(quiz.id)}
                    data-testid={`start-quiz-${quiz.id}`}
                  >
                    Start Quiz <ChevronRight size={18} />
                  </button>
                </motion.div>
              ))}
            </div>
          </section>
        </>
      ) : results ? (
        <motion.section className="quiz-results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="quiz-results">
          <div className="results-header">
            <div className={`results-icon ${results.passed ? 'passed' : 'failed'}`}>
              {results.passed ? <Award size={56} /> : <RotateCcw size={56} />}
            </div>
            <h2>{results.passed ? 'Well Done!' : 'Keep Learning!'}</h2>
            <div className="score-display" data-testid="quiz-score">
              <span className="score-big">{results.score_percent}%</span>
              <span className="score-detail">{results.score}/{results.total} correct</span>
            </div>
          </div>

          <div className="results-breakdown">
            {results.results.map((r, i) => (
              <div key={r.question_id} className={`result-item ${r.is_correct ? 'correct' : 'wrong'}`} data-testid={`result-${r.question_id}`}>
                <div className="result-icon">
                  {r.is_correct ? <CheckCircle size={20} /> : <XCircle size={20} />}
                </div>
                <div className="result-content">
                  <p className="result-question">{r.question}</p>
                  {!r.is_correct && (
                    <p className="result-answer">Correct answer: <strong>{r.correct_answer.toUpperCase()}</strong></p>
                  )}
                  <p className="result-explanation">{r.explanation}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="results-actions">
            <button className="btn btn-outline" onClick={resetQuiz} data-testid="back-to-quizzes"><ArrowLeft size={18} /> Back to Quizzes</button>
            <button className="btn btn-primary" onClick={() => startQuiz(activeQuiz.id)} data-testid="retry-quiz"><RotateCcw size={18} /> Try Again</button>
          </div>
        </motion.section>
      ) : (
        <motion.section className="quiz-active" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="quiz-active">
          <div className="quiz-progress-bar">
            <div className="quiz-progress-fill" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
          </div>
          <div className="quiz-header-bar">
            <button className="btn btn-ghost" onClick={resetQuiz}><ArrowLeft size={18} /> Exit</button>
            <span className="quiz-counter">{currentQ + 1} / {questions.length}</span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div 
              key={currentQ} 
              className="question-container"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              data-testid={`question-${currentQ}`}
            >
              <h2 className="question-text">{questions[currentQ]?.question}</h2>
              <div className="options-grid">
                {Object.entries(questions[currentQ]?.options || {}).map(([key, value]) => (
                  <button
                    key={key}
                    className={`option-btn ${answers[questions[currentQ].id] === key ? 'selected' : ''}`}
                    onClick={() => selectAnswer(questions[currentQ].id, key)}
                    data-testid={`option-${key}`}
                  >
                    <span className="option-key">{key.toUpperCase()}</span>
                    <span className="option-text">{value}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="quiz-nav">
            <button className="btn btn-outline" disabled={currentQ === 0} onClick={() => setCurrentQ(c => c - 1)}>Previous</button>
            {currentQ < questions.length - 1 ? (
              <button 
                className="btn btn-primary" 
                disabled={!answers[questions[currentQ]?.id]}
                onClick={() => setCurrentQ(c => c + 1)}
                data-testid="next-question-btn"
              >
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button 
                className="btn btn-primary" 
                disabled={Object.keys(answers).length < questions.length || submitting}
                onClick={submitQuiz}
                data-testid="submit-quiz-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            )}
          </div>
        </motion.section>
      )}

      <style>{`
        .quiz-page { min-height: 100vh; padding-bottom: 60px; }
        .quiz-hero { text-align: center; padding: 60px 24px 40px; }
        .quiz-hero-content { max-width: 600px; margin: 0 auto; }
        .quiz-hero-content svg { color: var(--primary); margin-bottom: 16px; }
        .quiz-hero-content h1 { font-size: 2rem; margin: 0 0 12px; }
        .quiz-hero-content p { color: var(--text-muted); font-size: 1.1rem; }
        .quiz-list { max-width: 900px; margin: 0 auto; padding: 0 24px; }
        .quiz-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
        .quiz-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          transition: border-color 0.2s;
        }
        .quiz-card:hover { border-color: var(--primary); }
        .quiz-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
        .quiz-level {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .level-beginner { background: rgba(0,255,148,0.15); color: var(--primary); }
        .level-intermediate { background: rgba(255,215,0,0.15); color: var(--gold); }
        .level-advanced { background: rgba(255,99,71,0.15); color: #ff6347; }
        .quiz-count { font-size: 13px; color: var(--text-muted); }
        .quiz-card h3 { margin: 0 0 8px; font-size: 1.15rem; }
        .quiz-card p { color: var(--text-muted); font-size: 14px; margin: 0 0 20px; flex: 1; }
        .quiz-start-btn { display: flex; align-items: center; gap: 8px; justify-content: center; }
        .quiz-active { max-width: 700px; margin: 0 auto; padding: 0 24px; }
        .quiz-progress-bar {
          height: 4px;
          background: var(--border);
          border-radius: 2px;
          margin: 20px 0;
          overflow: hidden;
        }
        .quiz-progress-fill {
          height: 100%;
          background: var(--primary);
          transition: width 0.3s ease;
          border-radius: 2px;
        }
        .quiz-header-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; }
        .quiz-counter { font-size: 14px; color: var(--text-muted); font-weight: 600; }
        .question-container { margin-bottom: 40px; }
        .question-text { font-size: 1.3rem; margin: 0 0 30px; line-height: 1.5; }
        .options-grid { display: flex; flex-direction: column; gap: 12px; }
        .option-btn {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: var(--bg-paper);
          border: 2px solid var(--border);
          border-radius: 12px;
          color: var(--text-secondary);
          cursor: pointer;
          text-align: left;
          font-size: 15px;
          transition: all 0.2s;
        }
        .option-btn:hover { border-color: var(--primary); background: rgba(0,255,148,0.05); }
        .option-btn.selected { border-color: var(--primary); background: rgba(0,255,148,0.1); color: var(--text-primary); }
        .option-key {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: var(--bg-subtle);
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
        }
        .option-btn.selected .option-key { background: var(--primary); color: var(--bg-base); }
        .quiz-nav { display: flex; justify-content: space-between; gap: 12px; }
        .quiz-results { max-width: 700px; margin: 0 auto; padding: 40px 24px; }
        .results-header { text-align: center; margin-bottom: 40px; }
        .results-icon { margin-bottom: 16px; }
        .results-icon.passed svg { color: var(--primary); }
        .results-icon.failed svg { color: var(--warning); }
        .results-header h2 { font-size: 1.5rem; margin: 0 0 20px; }
        .score-display { display: flex; flex-direction: column; align-items: center; gap: 4px; }
        .score-big { font-size: 3rem; font-weight: 800; color: var(--primary); }
        .score-detail { font-size: 14px; color: var(--text-muted); }
        .results-breakdown { display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
        .result-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
        }
        .result-item.correct { border-left: 3px solid var(--primary); }
        .result-item.wrong { border-left: 3px solid var(--error); }
        .result-icon svg { flex-shrink: 0; }
        .result-item.correct .result-icon svg { color: var(--primary); }
        .result-item.wrong .result-icon svg { color: var(--error); }
        .result-content { flex: 1; }
        .result-question { margin: 0 0 6px; font-weight: 500; }
        .result-answer { margin: 0 0 6px; font-size: 13px; color: var(--error); }
        .result-explanation { margin: 0; font-size: 13px; color: var(--text-muted); }
        .results-actions { display: flex; gap: 12px; justify-content: center; }
        .quiz-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          gap: 16px;
          color: var(--text-muted);
        }
        .spinner { width: 40px; height: 40px; border: 3px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn-ghost { background: none; border: none; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 14px; padding: 8px; }
        .btn-ghost:hover { color: var(--text-primary); }
        @media (max-width: 768px) {
          .quiz-grid { grid-template-columns: 1fr; }
          .question-text { font-size: 1.1rem; }
        }
      `}</style>
    </div>
  );
};

export default QuizPage;
